import React, { useContext, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Box as Container,
  EmptyState,
  Pagination,
  RowsPerPageType,
  RowsSkeletonLoader,
  toast,
} from '@node-space/ui-components'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { Lottie } from 'components/Animations/SimpleLottie'
import { ContactsSearchListLoader } from 'components/Contacts/ContactsSearchListLoader'
import { PageHeader } from 'components/header/PageHeader'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { usePostContactCaseMutation } from 'hooks/mutations/usePostContactCaseMutation'
import { useContactCasesQuery } from 'hooks/queries/useContactCasesQuery'
import {
  contactActionAtom,
  contactCaseAtom,
  contactCaseCountAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { ContactsSheets } from 'pages/Contacts/components/ContactsSheets'
import { getRequiredContactFieldsForCryptoAddress } from 'pages/Contacts/utils/fiatInstruments'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { ContactActionIds } from 'types/atoms/atom.contacts'
import { BaseErrorResponse } from 'types/beneficiaries'
import { Contact, ContactCase, InstrumentType, PostContactCase } from 'types/contacts'
import { ActionIds } from 'types/types'
import { ChannelCreateDialog } from './ChannelCreateDialog'
import { ContactCaseList } from './ContactCaseList'

export const ContactCases = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [, setWalletAction] = useContext(WalletActionContext)

  const setContactAction = useSetAtom(contactActionAtom)
  const setContactCase = useSetAtom(contactCaseAtom)
  const setContactCaseCount = useSetAtom(contactCaseCountAtom)

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [contact, setContact] = useState<Contact>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPageType>(10)
  const [offset, setOffset] = useState(0)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)

  const { mutate, isPending: isPendingPostContactCase } = usePostContactCaseMutation()

  const {
    data: { results: contactCasesList, paging: pagination } = {},
    isPending,
    isError,
  } = useContactCasesQuery({ offset, max: Number(rowsPerPage), total: 0 })

  const showPagination: boolean = pagination?.total <= pagination?.max
  const showEmptyState = !isPending && !isError && !contactCasesList?.length
  const showErrorState = isError && !isPending

  useEffect(() => {
    if (pagination?.total) {
      setContactCaseCount(pagination?.total)
    }
  }, [pagination?.total])

  const handleChangePage = (newPage: number) => {
    setPage(newPage)
    const newOffset = newPage * Number(rowsPerPage)
    setOffset(newOffset)
  }

  const handleChangeRowsPerPage = (newRowsPerPage: RowsPerPageType) => {
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    setOffset(0)
  }

  const handleContactSelect = (contact: Contact, contactCase: ContactCase) => {
    const requiredFields = getRequiredContactFieldsForCryptoAddress(contact)
    if (requiredFields.length) {
      setContactAction({
        actionId: ContactActionIds.CONTACT,
        header: t('contacts.updateContact'),
        onClose: () => {
          queryClient.invalidateQueries({ queryKey: ['contacts'] })
          queryClient.refetchQueries({ queryKey: [reactQueryName.CONTACT_CASES] })
          setIsContactSheetOpen(false)
        },
        meta: {
          keepUpdatedContact: true,
          flowType: InstrumentType.CRYPTO,
          contactRequiredFields: {
            requiredFields,
            calloutMessage: t('contacts.cryptoAccountRequiredAdditionalContactDetails'),
          },
        },
      })
      setIsContactSheetOpen(true)
      return
    }

    const data: PostContactCase = {
      contactId: contact?.id,
      externalId: contactCase?.externalId,
    }
    mutate(data, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACT_CASES] })
        queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACT_CASE_COUNT] })
        setIsSheetOpen(false)
        toast({
          header: t('payments.channelPayments.contactAssigned', { contactName: contact?.name }),
          variant: 'success',
        })
        setContact(contact)
        setIsDialogOpen(true)
      },
      onError: (error: BaseErrorResponse) => {
        logSentryErrorAndGetEventId(
          `Failed API request to /payments/v1/travel-rule-rfis/${data?.externalId}/assign-contact with status ${error?.status} - postContactCase`,
          error,
          {
            data,
          }
        )
        toast({
          header: t('contacts.contactCouldNotBeAssigned'),
          body: t('contacts.contactAssignTechnicalError'),
          variant: 'error',
        })
      },
    })
  }

  const handleAssignContactClick = (contactCase: ContactCase) => {
    setContactCase(contactCase)
    setWalletAction({
      actionId: ActionIds.CONTACT_ASSIGN,
      header: t('contacts.whoWillSendTheseFunds'),
      onClose: () => {
        setContactCase(null)
        setIsSheetOpen(false)
      },
      meta: {
        handleContactSelect: (contact: Contact) => handleContactSelect(contact, contactCase),
      },
    })
    setIsSheetOpen(true)
  }

  return (
    <Box>
      {!showEmptyState && !showErrorState && (
        <PageHeader description={t('contacts.noMissingContactsDescription')} />
      )}

      {/* Show error state */}
      {showErrorState && (
        <EmptyState
          testId="error-state"
          LottiePlayer={<Lottie src="connection-error" />}
          title={t('defaultError')}
          description={
            <>
              {`${t('requestErrors.comeBackLater')} `}
              <Button variant="ghost" noPadding onClick={() => window.location.reload()}>
                {t('requestErrors.refreshPage')}
              </Button>
            </>
          }
        />
      )}
      {/* Show content state */}
      {!isError && isPending && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

      {!isError && !!contactCasesList?.length && !isPending && (
        <Box className="w-full">
          <ContactCaseList
            contactCases={contactCasesList}
            handleAssignContactClick={handleAssignContactClick}
          />
        </Box>
      )}

      {!isError && !!pagination && !showPagination && (
        <Container className={clsx({ 'opacity-25': isPending })}>
          <Pagination
            testId="contact-cases-pagination"
            page={page}
            rowsPerPage={rowsPerPage}
            total={pagination?.total ?? 0}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Container>
      )}
      {/* Show empty state */}
      {showEmptyState && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('contacts.noMissingContacts')}
          description={t('contacts.noMissingContactsDescription')}
        />
      )}

      <WalletSheet isSheetOpen={isSheetOpen} />
      {isPendingPostContactCase && <ContactsSearchListLoader />}
      <ChannelCreateDialog
        contact={contact}
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
      />
      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet={true} />
    </Box>
  )
}
